
import { createContext, useContext, ReactNode, useState, useEffect } from "react";

// Define a simple, reliable default branding object
const defaultBranding = {
  companyName: "Agent Matcher",
  logoUrl: "/lovable-uploads/a9d4fe12-5eb2-4884-936a-3da184479c3f.png",
  primaryColor: "#0ea5e9",
  secondaryColor: "#f59e0b",
  brandDescription: "Connecting real estate agents with clients"
};

type BrandingContextType = {
  branding: typeof defaultBranding;
  updateBranding: (newBranding: Partial<typeof defaultBranding>) => void;
  isLoaded: boolean;
};

const BrandingContext = createContext<BrandingContextType>({
  branding: defaultBranding,
  updateBranding: () => {},
  isLoaded: true // Always loaded
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ 
  children 
}: { 
  children: ReactNode;
}) => {
  // Start with default branding always
  const [branding, setBranding] = useState(defaultBranding);
  
  // Always consider branding as loaded
  const [isLoaded, setIsLoaded] = useState(true);

  useEffect(() => {
    // Simple approach: try to load from localStorage
    try {
      const savedSettings = localStorage.getItem("brandingSettings");
      if (savedSettings) {
        const parsedSettings = JSON.parse(savedSettings);
        setBranding({...defaultBranding, ...parsedSettings});
      }
    } catch (error) {
      console.error("Failed to load branding settings:", error);
      // Continue with default branding on error
    }
  }, []);

  const updateBranding = (newBranding: Partial<typeof branding>) => {
    try {
      const updated = { ...branding, ...newBranding };
      setBranding(updated);
      localStorage.setItem("brandingSettings", JSON.stringify(updated));
    } catch (error) {
      console.error("Failed to save branding to localStorage:", error);
      // Continue without saving on error
    }
  };

  return (
    <BrandingContext.Provider value={{ branding, updateBranding, isLoaded }}>
      {children}
    </BrandingContext.Provider>
  );
};
